/**
 * Web billing hooks (ADR-0022 / T5.2).
 *
 * `usePremiumPlan` reads the premium plan from the public pricing source so copy
 * and the checkout planKey come from `pricing_plan` (never hard-coded, ADR-0022 §6).
 * `useStartCheckout` POSTs to /me/billing/checkout and redirects to the RC Web
 * Billing hosted checkout. A 409 means the user is already premium (the dedupe
 * guard) — surfaced to the caller so the UI can route them kindly.
 */
import type { components } from '@nutians/api-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../api.js';

type PricingPlan = components['schemas']['PublicPricingResponse']['plans'][number];
type BillingInterval = 'monthly' | 'annual';

export const PREMIUM_PLAN_KEY = 'premium';

/** Fetch the premium plan from the public pricing source (for price + planKey). */
export function usePremiumPlan() {
  return useQuery({
    queryKey: ['public-pricing', 'premium'],
    queryFn: async (): Promise<PricingPlan | null> => {
      const { data, error } = await api.GET('/public/pricing');
      if (error || !data) throw new Error('GET /public/pricing failed');
      return (
        data.plans.find((p) => p.planKey === PREMIUM_PLAN_KEY) ??
        data.plans.find((p) => p.highlight && p.priceMonthlyEur != null) ??
        null
      );
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Format a monthly price for upgrade copy, e.g. "€8.99/mo". Null → no price shown. */
export function formatMonthlyPrice(plan: PricingPlan | null | undefined): string | null {
  if (!plan || plan.priceMonthlyEur == null) return null;
  return `€${plan.priceMonthlyEur.toFixed(2)}/mo`;
}

export class AlreadyPremiumError extends Error {
  constructor() {
    super('already_premium');
    this.name = 'AlreadyPremiumError';
  }
}

/**
 * Start a web checkout. On success, redirects the browser to the RC-hosted
 * checkout URL. Throws AlreadyPremiumError on 409 so the caller can route to
 * Settings instead of nagging.
 */
export function useStartCheckout() {
  return useMutation({
    mutationFn: async (vars: { planKey?: string; interval?: BillingInterval } = {}) => {
      const { data, error, response } = await api.POST('/me/billing/checkout', {
        body: { planKey: vars.planKey ?? PREMIUM_PLAN_KEY, interval: vars.interval },
      });
      if (response.status === 409) throw new AlreadyPremiumError();
      if (error || !data) throw new Error('Could not start checkout. Please try again.');
      return data.checkoutUrl;
    },
    onSuccess: (checkoutUrl) => {
      window.location.assign(checkoutUrl);
    },
  });
}
